import React, { RefObject, useLayoutEffect, useMemo, useState } from 'react';
import { Box, keyframes, useMediaQuery, useTheme } from '@mui/material';

interface ConfettiProps {
  show?: boolean;
  /** The toast the confetti bursts out of. */
  anchorRef: RefObject<HTMLElement | null>;
}

const PIECES = 24;

/**
 * Up, then down: decelerate into the peak, accelerate out of it — close
 * enough to a throw under gravity. Each piece sets its own --dx/--rise/
 * --fall/--spin, so one keyframe serves them all.
 */
const burst = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg);
    animation-timing-function: cubic-bezier(0.15, 0.7, 0.35, 1);
  }
  30% {
    transform: translate(calc(var(--dx) * 0.45), var(--rise))
      rotate(calc(var(--spin) * 0.3));
    animation-timing-function: cubic-bezier(0.55, 0, 0.85, 0.5);
  }
  100% {
    transform: translate(var(--dx), var(--fall)) rotate(var(--spin));
  }
`;

const fade = keyframes`
  0%, 75% { opacity: 1; }
  100% { opacity: 0; }
`;

/**
 * Where the toast sits once it has landed. Its entrance animation is a
 * transform, which getBoundingClientRect would include (on desktop it starts
 * a full width off to the right), so this goes by the layout box instead.
 */
function restingBox(el: HTMLElement) {
  const parent = el.offsetParent as HTMLElement | null;
  const p = parent?.getBoundingClientRect() ?? { left: 0, top: 0 };
  return {
    left: p.left + el.offsetLeft - (parent?.scrollLeft ?? 0),
    top: p.top + el.offsetTop - (parent?.scrollTop ?? 0),
    width: el.offsetWidth,
  };
}

const rand = (min: number, max: number) => min + Math.random() * (max - min);

const Confetti: React.FC<ConfettiProps> = ({ show = false, anchorRef }) => {
  const theme = useTheme();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [box, setBox] = useState<ReturnType<typeof restingBox> | null>(null);

  useLayoutEffect(() => {
    setBox(show && anchorRef.current ? restingBox(anchorRef.current) : null);
  }, [show, anchorRef]);

  const pieces = useMemo(() => {
    const colors = Object.values(theme.palette).flatMap((color) =>
      typeof color === 'object' && color.main
        ? [color.main, color.light, color.dark]
        : [],
    );
    return Array.from({ length: PIECES }, () => ({
      x: Math.random(),
      size: rand(6, 11),
      color: colors[Math.floor(Math.random() * colors.length)],
      round: Math.random() > 0.5,
      dx: rand(-140, 140),
      rise: -rand(90, 230),
      fall: rand(260, 480),
      spin: rand(-720, 720),
      duration: rand(1.6, 2.4),
      delay: rand(0, 0.15),
    }));
    // A fresh spray each time it's shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, theme.palette]);

  if (!show || !box || reduceMotion) return null;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        left: box.left,
        top: box.top,
        width: box.width,
        height: 0,
        pointerEvents: 'none',
        zIndex: 10001,
        overflow: 'visible',
      }}
    >
      {pieces.map((p, i) => (
        <Box
          key={i}
          style={
            {
              '--dx': `${p.dx}px`,
              '--rise': `${p.rise}px`,
              '--fall': `${p.fall}px`,
              '--spin': `${p.spin}deg`,
            } as React.CSSProperties
          }
          sx={{
            position: 'absolute',
            left: `${p.x * 100}%`,
            top: 0,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.round ? '50%' : '2px',
            animation: `${burst} ${p.duration}s ${p.delay}s both, ${fade} ${p.duration}s linear ${p.delay}s both`,
          }}
        />
      ))}
    </Box>
  );
};

export default Confetti;
